import type { ProductoInfo360Maestro } from './components/ArticuloInfo360Header';
import type { ArticuloInfo360Indicadores, ArticuloInfo360StockDeposito } from './types';

export interface StockOperativoGlobal {
  produccion: number | null | undefined;
  oc: number | null | undefined;
  reservado: number | null | undefined;
  dispVenta: number | null | undefined;
  total: number | null | undefined;
}

function esTipoKit(tipoArticulo?: string | null): boolean {
  return String(tipoArticulo ?? '').trim().toUpperCase() === 'KIT';
}

function sumarCampoDeposito(
  depositos: ArticuloInfo360StockDeposito[] | undefined,
  campo: 'stockProduccion' | 'stockOrdenCompra' | 'stockPendienteOcRecibir',
): number | null {
  if (!depositos?.length) return null;
  return depositos.reduce((acc, dep) => acc + (Number(dep[campo]) || 0), 0);
}

/**
 * Stock operativo global.
 * - Artículos normales: info-360 primero, maestro como fallback.
 * - Kits: no tienen stock físico propio; el disponible es cuántos kits se pueden
 *   armar con los componentes (mínimo de floor(stock_componente / cant_por_kit)).
 *   Solo se usa info-360 / kitStock; nunca cantidad/stockVenta del maestro.
 */
export function resolveStockOperativo(
  producto: ProductoInfo360Maestro,
  data: ArticuloInfo360Indicadores | null,
): StockOperativoGlobal {
  const esKit = esTipoKit(producto.tipoArticulo);
  const kitDisponible = data?.kitStock?.disponible;

  const dispVenta = data?.stockDisponibleVenta
    ?? (esKit && kitDisponible != null ? kitDisponible : undefined)
    ?? (esKit ? undefined : producto.stockVenta);

  const total = data?.stockNeto
    ?? (esKit && kitDisponible != null ? kitDisponible : undefined)
    ?? (esKit ? undefined : producto.cantidad);

  const reservado = data?.stockReservado ?? producto.stockPedido;
  const oc = data?.stockPendienteOc ?? producto.cantOrdenCompra;

  const produccionPorDeposito = sumarCampoDeposito(data?.stockPorDeposito, 'stockProduccion');
  const produccion = producto.stockProduccion ?? produccionPorDeposito;

  return { produccion, oc, reservado, dispVenta, total };
}
